// Bejelentkezés — az Atlas login-mintájára (Odin iam a BFF-proxyn át): access-token a store-ba
// (memória), refresh httpOnly cookie-ba. Bal (≥lg): zöld brand-panel; jobb: űrlap. Mobilon csak űrlap.
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginBodySchema } from '@morgens-hub/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { api } from '@/lib/api';
import { isSafeRedirectTarget } from '@/lib/auth';
import { useAuthStore } from '@/lib/auth-store';

// Audit T14: a login `?redirect=` search-paramot fogad — sikeres belépés után oda navigál (lásd
// `isSafeRedirectTarget` az open-redirect-védelemért).
const SearchSchema = z.object({ redirect: z.string().optional() });

export const Route = createFileRoute('/login')({
  validateSearch: SearchSchema,
  component: LoginPage,
});

type LoginValues = z.infer<typeof LoginBodySchema>;

function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect: target } = Route.useSearch();
  const setToken = useAuthStore((s) => s.setToken);
  const [rememberMe, setRememberMe] = useState(true);
  const login = api.login.useMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginValues>({
    resolver: zodResolver(LoginBodySchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit((values) => {
    login.mutate(
      { body: { ...values, rememberMe } },
      {
        // ts-rest + react-query: az onSuccess-be csak 2xx jut (a contract szerint itt csak 200 lehet).
        onSuccess: (res) => {
          setToken(res.body.accessToken);
          queryClient.clear();
          void navigate({ to: isSafeRedirectTarget(target) ? target : '/' });
        },
        // ts-rest: a nem-2xx (a contractban definiált 401 is) onError-ra fut — itt bontunk státusz szerint
        onError: (err) => {
          const status = (err as { status?: number } | null)?.status;
          const key =
            status === 401
              ? 'login.errInvalid'
              : status === 429
                ? 'login.errRateLimited'
                : status === 503
                  ? 'login.errUnavailable'
                  : 'login.errFailed';
          toast.error(t(key));
        },
      },
    );
  });

  return (
    <main className="bg-background grid h-screen w-screen lg:grid-cols-2">
      {/* Űrlap — desktopon JOBB oldalon (a brand-panel balra) */}
      <div className="flex items-center justify-center p-8 lg:order-2">
        <div className="animate-in fade-in slide-in-from-bottom-1 w-full max-w-[360px] duration-300">
          {/* Branding az űrlapon CSAK mobilon (desktopon a bal panel viszi) */}
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <span className="bg-primary size-3 rounded-full" aria-hidden />
            <span className="text-[22px] font-semibold">{t('common.appName')}</span>
          </div>

          <h1 className="mb-2 text-[26px] font-semibold tracking-[-0.015em]">{t('login.title')}</h1>
          <p className="text-muted-foreground mb-8 text-base">{t('login.subtitle')}</p>

          <form onSubmit={onSubmit}>
            <div className="mb-3.5 space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium">
                {t('login.email')}
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="nev@morgens.hu"
                className="h-[38px]"
                {...register('email')}
              />
              {errors.email && <p className="text-destructive text-xs">{errors.email.message}</p>}
            </div>
            <div className="mb-3.5 space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">
                  {t('login.password')}
                </Label>
                <span
                  className="text-muted-foreground hover:text-foreground cursor-pointer text-xs hover:underline"
                  title={t('common.comingSoon')}
                >
                  {t('login.forgot')}
                </span>
              </div>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                className="h-[38px]"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-destructive text-xs">{errors.password.message}</p>
              )}
            </div>

            {/* „Emlékezz rám": pipa → perzisztens session (30 nap, csúszó ablak); nincs pipa → session-cookie. */}
            <label className="text-md mb-5 flex cursor-pointer items-center gap-2">
              <Checkbox checked={rememberMe} onCheckedChange={(v) => setRememberMe(v === true)} />
              {t('login.remember')}
            </label>

            <Button type="submit" size="lg" className="w-full" disabled={login.isPending}>
              {login.isPending ? (
                t('login.submitting')
              ) : (
                <>
                  {t('login.submit')} <ArrowRightIcon />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Zöld brand-panel — desktopon BAL oldalon (≥lg): zöld gradiens + radiálok + rács. */}
      <div className="relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:order-1 lg:flex">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 15% 20%, rgb(var(--accent-500) / 0.35), transparent 45%), radial-gradient(circle at 85% 75%, rgb(var(--accent-300) / 0.3), transparent 50%), linear-gradient(135deg, var(--accent-grad-from), var(--accent-grad-to))',
          }}
        />
        <div className="auth-grid-bg" />
        <div className="from-accent-700/90 via-accent-700/75 to-accent-600/60 pointer-events-none absolute inset-0 bg-gradient-to-br" />

        <div className="relative z-10 flex items-center gap-2">
          <span className="size-3 rounded-full bg-white" aria-hidden />
          <span className="text-[22px] font-semibold">{t('common.appName')}</span>
        </div>

        <div className="relative z-10">
          <h2 className="mb-4 text-4xl font-semibold leading-[1.1] tracking-[-0.02em]">
            {t('login.heroLine1')}
            <br />
            {t('login.heroLine2')}
          </h2>
          <p className="mb-8 max-w-[440px] text-[15px] leading-relaxed text-white/70">
            {t('login.heroBody')}
          </p>
        </div>

        <div className="relative z-10 text-xs text-white/40">© 2026 MORGENS Design Kft.</div>
      </div>
    </main>
  );
}
